import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { debounce } from "throttle-debounce"
import Box from "./Box"
import useScrollTrigger from "./hooks/useScrollTrigger"
import { m } from "framer-motion"

interface RevealTextAnimationProps {
  text: string
  threshold?: number
  isTriggered?: boolean
  delay?: number
}

const RevealTextAnimation: FC<RevealTextAnimationProps> = memo(
  ({ text, threshold = 0.5, isTriggered, delay = 0 }) => {
    const wrapperRef = useRef<HTMLDivElement | undefined>()
    const originalTextRef = useRef<any>()
    const [lines, setLines] = useState([])
    const { isTriggered: localIsTriggered } = useScrollTrigger({
      wrapperRef,
      threshold,
    })
    const finalIsTriggered =
      isTriggered != null ? isTriggered : localIsTriggered

    const splitLines = () => {
      if (!originalTextRef.current) return
      const originalTextEl = originalTextRef.current
      originalTextEl.innerHTML = ""

      // Split text into words by word boundary and custom delimiter for chinese/korean/japanese characters (backslash)
      const chineseJapaneseKoreanCharacterRange =
        /([\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff66-\uff9f\u3131-\uD79D]{1})/g
      const tmpText = text.replace(
        chineseJapaneseKoreanCharacterRange,
        "\\$1\\"
      )
      // const words = tmpText
      //   .split(/b|\\/)
      //   .filter(word => word !== "\\")
      //   .map(word => word.replace(/\\/g, ""))

      const words = tmpText
        ?.match(/\S+|\s+|\\/g)
        ?.filter(word => word !== "\\")
        ?.map(word => word.replace(/\\/g, ""))

      let tmpLines = []
      let currentLine = 0
      let previousHeight = 0
      let lineHeight = 0
      words?.forEach((word, i) => {
        originalTextEl.innerHTML += word

        const heightInPx = window
          .getComputedStyle(originalTextEl)
          .getPropertyValue("height")
        const height = heightInPx ? parseFloat(heightInPx.replace("px", "")) : 0

        // Some characters doesn't have exactly the same height so giving it a range here
        if (height - previousHeight > (lineHeight * 1) / 2) {
          currentLine += 1
          previousHeight = height

          // store the first line height
          if (i === 0) lineHeight = previousHeight
        }

        tmpLines[currentLine] = (tmpLines[currentLine] || "") + word
      })

      tmpLines = tmpLines.slice(1)
      setLines(tmpLines)
    }

    const debounceSplitLines = useCallback(debounce(150, splitLines), [text])

    useEffect(() => {
      window.addEventListener("resize", debounceSplitLines)
      return () => {
        window.removeEventListener("resize", debounceSplitLines)
      }
    })
    useEffect(debounceSplitLines, [debounceSplitLines])

    return (
      <Box position="relative" ref={wrapperRef}>
        <Box
          position={lines.length > 0 ? "absolute" : "relative"}
          top={0}
          width="100%"
          ref={originalTextRef}
          opacity={0}
          letterSpacing={["-3px"]}
        >
          {/* SEO */}
          {text}
        </Box>
        {lines.map((line, i) => (
          <Box key={line} overflow="hidden">
            <m.div
              animate={{ y: finalIsTriggered ? 0 : "100%" }}
              transition={{
                delay: finalIsTriggered
                  ? delay + 0.1 * i
                  : delay + 0.1 * (lines.length - 1 - i),
                bounce: 0,
              }}
            >
              {line}
            </m.div>
          </Box>
        ))}
      </Box>
    )
  }
)
RevealTextAnimation.displayName = "RevealTextAnimation"
export default RevealTextAnimation
